import { ClipboardList } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `apps/web-admin/src/routes/{dashboard,orders}/`: button labels and
 * states quoted verbatim from `dashboard-day-page.tsx`, `order-list-page.tsx`,
 * `order-form-page.tsx`, `order-detail-page.tsx`,
 * `order-template-list-page.tsx`, `change-request-list-page.tsx` and
 * `order-change-requests-tab.tsx`. `requiredPermission: 'order.read'`
 * matches the `NavPermission` that gates both "Tablero del día" (via the
 * broader `dashboard.view`, a subset of `order.read`'s roles) and
 * "Pedidos" in `layout/navigation.ts`.
 */
export const ORDERS_MODULE: GuideModule = {
  id: 'orders',
  title: 'Pedidos y Tablero del día',
  icon: ClipboardList,
  requiredPermission: 'order.read',
  priority: 7,
  summary:
    'El flujo diario es simple: las empresas cargan o les cargás un pedido antes de la hora de corte del contrato. El Tablero del día te muestra en todo momento quién ya pidió y quién falta. Cuando llega el momento, hacés clic en "Cerrar corte": eso confirma todos los pedidos enviados y cancela los que quedaron en borrador, para esa fecha. A partir de ahí, un pedido confirmado ya no se edita directamente: cualquier cambio se gestiona con una solicitud de cambio que alguien con permiso tiene que aprobar. Las plantillas de pedido te ahorran cargar a mano los pedidos que se repiten siempre igual.',
  steps: [
    {
      title: 'Cargar un pedido nuevo',
      description:
        'Andá a Pedidos y hacé clic en "Nuevo pedido". Elegí empresa, sede, "Punto de venta" (si la empresa tiene contrato en más de uno) y fecha de servicio: el formulario arma automáticamente una fila por cada posición del menú que tenga un plato publicado ese día, agrupadas por tipo de servicio, ya con el plato del día cargado; vos solo cargás la cantidad (y una nota, si hace falta). Una posición sin plato publicado esa fecha no aparece. Guardá con "Guardar borrador".',
      keyTerms: ['pedido', 'pedido-borrador', 'posicion-del-menu'],
    },
    {
      title: 'Enviar el pedido antes del corte',
      description:
        'Desde el detalle del pedido en borrador, hacé clic en "Enviar" y confirmá. Un pedido enviado todavía se puede editar hasta que se cierre el corte.',
      keyTerms: ['pedido-enviado', 'cierre-de-corte'],
    },
    {
      title: 'Revisar el Tablero del día antes de cerrar',
      description:
        'El Tablero del día muestra qué empresas ya pidieron y cuáles faltan. Si hace falta, hacé clic en "Recordar a los que faltan" para avisarles.',
      keyTerms: ['tablero-del-dia'],
    },
    {
      title: 'Filtrar el Tablero con las tarjetas de resumen',
      description:
        'Arriba de la lista, 4 tarjetas resumen muestran "Faltantes", "Unidades totales", "Enviados / Borradores" y "Alertas". Todas menos "Unidades totales" son clickeables: hacé clic en una para filtrar la lista de abajo a esa condición. Con un filtro activo, un texto "Mostrando: ..." aparece sobre la lista con un enlace "Ver todo" para sacarlo.',
      keyTerms: ['tablero-del-dia'],
    },
    {
      title: 'Cerrar el corte',
      description:
        'Si el cierre automático está apagado, o querés adelantarte (o cerrar un día anterior, que el automático no toca), hacé clic en "Cerrar corte" y confirmá. Se confirman los pedidos enviados y se cancelan los que quedaron en borrador para esa fecha. No se puede deshacer.',
      keyTerms: ['cierre-de-corte', 'pedido-confirmado'],
    },
    {
      title: 'Cierre automático a la hora de corte',
      description:
        'Con el "Cierre automático de pedidos" encendido (viene encendido), cada pedido se cierra solo a su propia hora de corte, sin que hagas nada: los enviados se confirman y los borradores se cancelan, y se avisa a cocina y operaciones igual que con el botón. Solo cierra pedidos de hoy en adelante: los de días anteriores no se cierran solos, se cierran a mano con "Cerrar corte". El Tablero del día indica "Cierre automático activo" (o "desactivado") junto al botón y marca con "Cierre automático" los pedidos cerrados así. Para apagarlo, andá a Configuración → Parámetros del sistema → Pedidos y desactivá "Cierre automático de pedidos"; el botón "Cerrar corte" sigue disponible en ambos casos.',
      keyTerms: ['cierre-de-corte', 'hora-de-corte'],
    },
    {
      title: 'Solicitar un cambio en un pedido ya confirmado',
      description:
        'Entrá al pedido confirmado, pestaña "Cambios", y hacé clic en "Solicitar cambio". Un usuario con permiso lo va a "Aprobar" (con una confirmación) o "Rechazar" desde "Solicitudes de cambio".',
      keyTerms: ['solicitud-de-cambio', 'pedido-confirmado'],
    },
    {
      title: 'Crear una plantilla de pedido',
      description:
        'Andá a "Plantillas", hacé clic en "Nueva plantilla", elegí empresa, sede y los días de la semana. La sección "Líneas de la plantilla" trae una fila fija por cada posición activa (sin plato: la plantilla no depende de una fecha), y solo cargás la cantidad de cada una.',
      keyTerms: ['plantilla-de-pedido', 'posicion-del-menu'],
    },
    {
      title: 'Generar pedidos desde una plantilla',
      description:
        'Andá a "Plantillas", hacé clic en "Generar pedidos ahora", elegí la fecha y confirmá con "Generar". Se crean los pedidos de las plantillas activas que cubran esa fecha, con la cantidad de cada posición contra el plato publicado ese día; una posición sin plato publicado esa fecha se omite y queda informada en el resultado. Se saltean las combinaciones que ya tienen un pedido cargado.',
      keyTerms: ['plantilla-de-pedido'],
    },
  ],
  states: [
    {
      value: 'DRAFT',
      label: 'Borrador',
      description: 'Todavía no fue enviado. Se puede editar libremente.',
    },
    {
      value: 'SUBMITTED',
      label: 'Enviado',
      description: 'Ya fue enviado, pero se puede editar hasta que se cierre el corte.',
    },
    {
      value: 'CONFIRMED',
      label: 'Confirmado',
      description: 'El corte ya se cerró. Producción ya lo ve.',
    },
    {
      value: 'CANCELLED',
      label: 'Cancelado',
      description: 'No se cocina.',
    },
  ],
  faq: [
    {
      question: '¿De dónde sale el precio de cada línea del pedido?',
      answer:
        'Junto al precio unitario de cada línea del detalle del pedido se indica el origen: "Contrato (menú)" si el contrato tiene un precio puntual para ese plato, "Contrato (servicio)" si usa el precio del contrato para ese tipo de servicio y "Catálogo" si el contrato no tiene ninguno de los dos y se usó el precio de catálogo del plato. El precio queda fijo en el pedido aunque después cambie el contrato o el catálogo. En los pedidos viejos, de antes de que se registrara el origen, se muestra un guion.',
    },
    {
      question: '¿Qué pasa si un cliente pide después del corte?',
      answer:
        'Ese pedido no entra en el cierre normal. Si tenés el permiso de excepción de corte, podés cargarlo igual; si no, tenés que esperar al día siguiente o pedirle a un administrador que lo cargue con esa excepción.',
    },
    {
      question: '¿Por qué se confirmó mi pedido solo?',
      answer:
        'Porque el "Cierre automático de pedidos" está encendido: a la hora de corte de cada pedido el sistema lo cierra solo (los enviados se confirman y los borradores se cancelan). En el Tablero del día aparece como "Cierre automático". Los días anteriores no se cierran solos, solo desde hoy en adelante. Si preferís cerrar siempre a mano, un administrador puede apagarlo en Configuración → Parámetros del sistema → Pedidos.',
    },
    {
      question: '¿Puedo editar un pedido que ya está confirmado?',
      answer:
        'No directamente. Tenés que hacer clic en "Solicitar cambio" desde el pedido, y alguien con permiso de aprobación lo tiene que aprobar.',
    },
    {
      question: '¿Qué pasa con los pedidos en borrador cuando cierro el corte?',
      answer: 'Se cancelan automáticamente. Por eso conviene enviarlos antes de cerrar.',
    },
    {
      question: '¿"Recordar a los que faltan" les envía un pedido automático?',
      answer:
        'No genera pedidos. Envía un aviso previo al corte a los contactos de Compras de cada empresa que todavía no pidió. Una empresa sin contacto de Compras cargado no recibe el aviso.',
    },
    {
      question: '¿Para qué sirve "Generar pedidos ahora" si ya tengo plantillas activas?',
      answer:
        'Las plantillas no generan pedidos solas: tenés que disparar la generación para una fecha puntual con este botón, y salta las combinaciones que ya tienen un pedido cargado.',
    },
    {
      question: '¿Por qué no veo todas las posiciones activas al cargar un pedido?',
      answer:
        'Solo aparece una fila por posición que tenga un plato publicado para esa fecha exacta en el Calendario. Si falta una, revisá primero si esa posición tiene un menú publicado ese día.',
    },
  ],
  tips: [
    'Revisá el Tablero del día antes de cerrar el corte: es más fácil completar lo que falta antes que gestionar solicitudes de cambio después.',
    'Cargá plantillas para las empresas con pedidos que se repiten siempre igual: te ahorra tiempo todos los días.',
    'Si necesitás cargar un pedido fuera de hora, fijate primero si tenés permiso de excepción de corte antes de pedir ayuda.',
    'Usá "Exportar CSV" desde Pedidos si necesitás analizar el listado del día en una planilla de cálculo.',
  ],
};
